import React from 'react';
import Table from 'lib/components/table.jsx';
import { capitalize } from 'lib/utils/string.js';
import { EditButton, DeleteButton } from 'src/components/row-buttons.jsx';
import { defineDefaults } from 'lib/helpers/props.js';
import FloatingTooltip from '#components/tooltip.jsx';
import { compareDate } from './date.js';
import { Icon } from '@iconify-icon/react';

const defaultColumnOptions = {
    omit: ['id'],
    sortable: true, // boolean or array
    toDetail: (row) => `../edit/${row.id}`,
    headerText: (accessor) => capitalize(accessor)
};

export function makePaginatedTableColumns(data, searchProps, options = {}) {
    const { searchParams, changeSorting } = searchProps;
    defineDefaults(options, defaultColumnOptions);
    const { omit, sortable, remove, toDetail, headerText, isLoading, users } = options;

    const datum = data?.pages?.[0].results?.[0];

    let columns = [];
    let templateColumns = [];

    if (!datum) return { columns, templateColumns: '' };

    if (toDetail) {
        columns.push({
            id: 'detail',
            Cell: ({ row }) => {
                if (isLoading) return <EditButton className={'table-edit-btn'} disabled />;
                return (
                    <FloatingTooltip content={_t.global.edit()} placement="top">
                        <EditButton className={'table-edit-btn'} to={toDetail(row)} />
                    </FloatingTooltip>
                );
            },
            width: 'min-content'
        });

        templateColumns.push('min-content');
    }
    if (remove) {
        columns.push({
            id: 'delete',
            Cell: ({ row }) => {
                return (
                    <FloatingTooltip content={_t.global.delete()}>
                        <DeleteButton className="text-red-700" onClick={() => remove(row)} />
                    </FloatingTooltip>
                );
            },
            width: 'min-content'
        });

        templateColumns.push('min-content');
    }

    Object.keys(datum).forEach((accessor) => {
        if (Array.isArray(omit) && omit.includes(accessor)) return;

        const isSortable = sortable === true || (Array.isArray(sortable) && sortable.includes(accessor));

        let column = {
            Header: headerText(accessor),
            as: isSortable ? Table.SortableHeader : Table.Header,
            accessor,
            sortable: isSortable
        };

        if (isSortable) {
            column.props = {
                sortDirection: searchParams?.sortDirection,
                isSorting: accessor === searchParams?.sortField,
                onSort: () => changeSorting(accessor)
            };
        }

        if (accessor === "tasks") {
            // aggiunta possibilità di renderizzare righe con array
            column.Cell = ({ row }) => {
                const filteredDueDates = row[accessor]
                    .filter((item) => item.dueDate)
                    .map((item) => {
                        return {
                            name: item.name,
                            dueDate: item.dueDate
                        };
                    });

                const expiringTasks = filteredDueDates.filter((item) => {
                    const inputDate = item.dueDate;
                    const { isExpiring } = compareDate(inputDate);
                    return isExpiring;
                });

                const expiredTasks = filteredDueDates.filter((item) => {
                    const inputDate = item.dueDate;
                    const { isExpired } = compareDate(inputDate);
                    return isExpired;
                });

                const expiringTasksCount = expiringTasks.length;
                const expiredTasksCount = expiredTasks.length;

                return (
                    <div className="w-full flex gap-2">
                        {row[accessor].length ?? ''}
                        {(expiringTasksCount > 0 || expiredTasksCount > 0) && (
                            <div className="flex gap-1">
                                {expiringTasksCount > 0 && (
                                    <FloatingTooltip content={_t.crud.itinerary.headers.dueTasks()} placement="top">
                                        <Icon
                                            icon="ic:round-error-outline"
                                            width="1.2rem"
                                            height="1.2rem"
                                            className="text-amber-500"
                                        />
                                    </FloatingTooltip>
                                )}
                                {expiredTasksCount > 0 && (
                                    <FloatingTooltip content={_t.crud.itinerary.headers.expiredTasks()} placement="top">
                                        <Icon
                                            icon="ic:round-error-outline"
                                            width="1.2rem"
                                            height="1.2rem"
                                            className="text-red-500"
                                        />
                                    </FloatingTooltip>
                                )}
                            </div>
                        )}
                    </div>
                );
            };
        }

        // aggiunta possibilità di renderizzare righe con array
        if (accessor === "feedbacks") {
            column.Cell = ({ row }) => {
                const isSomeNotChecked = row[accessor].some((item) => !item.checked);

                return (
                    <div className="w-full flex gap-2">
                        {row[accessor].length ?? ''}
                        {(isSomeNotChecked) && (
                            <div className="flex gap-1">
                                <FloatingTooltip content={_t.crud.itinerary.headers.uncheckedFeedbacks()} placement="top">
                                    <Icon
                                        icon="ic:round-error-outline"
                                        width="1.2rem"
                                        height="1.2rem"
                                        className="text-amber-500"
                                    />
                                </FloatingTooltip>
                            </div>
                        )}
                    </div>
                );
            };
        }

        if (accessor === "assignedTo") {
            column.Cell = ({ row }) => {
                const user = users?.find((user) => user.id === row[accessor]);
                return user ? `${user.firstName} ${user.lastName}` : '';
            };
        }

        columns.push(column);
        templateColumns.push('auto');
    });

    return { columns, templateColumns: templateColumns.join(' ') };
}
